/* eslint-disable no-unsafe-optional-chaining */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RETAIL_BACKEND } from '@/constants';
import i18n from '@/translations/client-setup';
import {
    CarDetailsType,
    CitiesAreasData,
    GetBookingAndVehicleDetails,
    GetCarDetailsResponse,
    GetCarListing,
    GetCarShowroomCalendarResponse,
    GetCitiesAreas,
    IBodyType,
    IBodyTypeResult,
    IBookedVehicleDetails,
    IFinanceApplicationData,
    IMinAndMaxInterestRatesResult,
    IMinAndMaxPricesResult,
    ImperfectionReportResult,
    ImperfectionReportSection,
    InterestRate,
    IPopularBrnads,
    IPopularBrnadsResult,
    IVisitDetails,
    IVisitDetailsCheck,
    IVisitDetailsData,
    LenderWithInterestRates,
    MinAndMaxInterestRates,
    MinAndMaxPrices,
    ValidateTimeSlotResponse,
    VisitDetailsResponse,
} from '@/types';
import { baseQueryWrapper } from '@/libs/baseQueryWrapper.lib';
export interface ISearch {
    showroom: string;
    id: string;
}
export const interceptorApiSlice = createApi({
    reducerPath: 'retailInterceptor',
    baseQuery: baseQueryWrapper({ baseUrl: `${RETAIL_BACKEND}` }),
    tagTypes: ['Checkout'],
    endpoints: builder => ({
        bookSlot: builder.mutation<any, any>({
            query: data => ({
                url: '/bookings',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: any) => {
                return response?.bookingData?.data;
            },
        }),
    }),
});

const { useBookSlotMutation } = interceptorApiSlice;

// Define our single API slice object
export const apiSlice = createApi({
    // The cache reducer expects to be added at `state.retail` (default is state.api - this is optional)
    reducerPath: 'retail',
    baseQuery: fetchBaseQuery({
        baseUrl: RETAIL_BACKEND,
        prepareHeaders: headers => {
            headers.set('lang', `${i18n.language}`);
            return headers;
        },
    }),
    tagTypes: [
        'CarDetails',
        'CarListing',
        'Cities',
        'Checkout',
        'interestRates',
        'PopularBrands',
        'MaxAndMinPrices',
        'MinMaxInterestRates',
    ],
    // The "endpoints" represent operations and requests for this server
    endpoints: builder => ({
        // The getCarListing fetches all public cars
        getCarListing: builder.query<GetCarListing, void>({
            query: () => '/vehicles/retail/public',
            providesTags: ['CarListing'],
        }),
        getSimilarCars: builder.query<GetCarListing, string>({
            query: id => `/vehicles/retail/public/${id}/similar-cars`,
            providesTags: ['CarListing'],
        }),
        // fetch car details by car id
        getCarDetails: builder.query<
            CarDetailsType,
            { carId: string; accessKey?: string }
        >({
            query: ({ carId, accessKey }) => {
                return `vehicles/retail/public/${carId}${
                    accessKey ? `?accessKey=${accessKey}` : ''
                }`;
            },
            transformResponse: (response: GetCarDetailsResponse) =>
                response.data,
            providesTags: ['CarDetails'],
        }),
        // fetch cities and areas
        getCitiesAreas: builder.query<CitiesAreasData[], void>({
            query: () => '/cities',
            transformResponse: (response: GetCitiesAreas) => response.data,
            providesTags: ['Cities'],
        }),
        // fetch car imperfection report by car id
        getImperfectionReport: builder.query<
            ImperfectionReportSection[],
            string
        >({
            query: id => `/imperfections/public/${id}`,
            transformResponse: (response: ImperfectionReportResult) =>
                response.data,
            providesTags: ['CarDetails'],
        }),
        getImperfectionReportNew: builder.query<
            ImperfectionReportSection[],
            string
        >({
            query: id => `/vehicles/${id}/public/imperfections`,
            transformResponse: (response: ImperfectionReportResult) =>
                response.data,
            providesTags: ['CarDetails'],
        }),
        // get popular brnads for home page
        getPopularBrands: builder.query<IPopularBrnads[], void>({
            query: () => '/vehicles/popular-brands',
            transformResponse: (response: IPopularBrnadsResult) =>
                response.data,
            providesTags: ['PopularBrands'],
        }),
        // get body types for home page
        getBodyTypes: builder.mutation<IBodyType[], void>({
            query: () => '/vehicles/body-types',
            transformResponse: (response: IBodyTypeResult) => response.data,
            invalidatesTags: ['CarListing', 'PopularBrands'],
        }),
        // fetch showrrom and calendar details by car id
        getCarShowroomCalendar: builder.query<
            any,
            {
                id: string;
                bookingType: 'SEE' | 'RESERVE';
                reservationType: 'HOME' | 'SHOWROOM';
            }
        >({
            query: ({ id, reservationType, bookingType }) =>
                `showrooms/calendar/${id}/${bookingType}/${reservationType}`,
            transformResponse: (response: GetCarShowroomCalendarResponse) =>
                response.data,

            providesTags: ['CarDetails'],
        }),

        // fetch booking and vehicle data after payment using tempId
        getBookingAndVehicleDetails: builder.query<any, string>({
            query: id => `/bookings/${id}/status`,
            transformResponse: (response: GetBookingAndVehicleDetails) =>
                response.data,
            providesTags: ['Checkout'],
        }),
        validateTimeSlot: builder.mutation<ValidateTimeSlotResponse, any>({
            query: ({
                vehicle_id,
                available_at,
                day_id,
                time_slot_id,
                showroom_id,
            }) => ({
                url: `showrooms/calendar/validate/${vehicle_id}/${showroom_id}/${available_at}/${day_id}/${time_slot_id}`,
                method: 'GET',
            }),
        }),
        sendOTP: builder.mutation<any, any>({
            query: phone => ({
                url: 'clients/otp/send',
                method: 'POST',
                body: { phone },
            }),
            transformResponse: () => {
                return true;
            },
        }),
        bookSlot: builder.mutation<any, any>({
            query: data => ({
                url: 'bookings',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: any) => {
                return response?.bookingData?.data;
            },
        }),
        getInterestRates: builder.query<InterestRate[], void>({
            query: () => '/interest-rates/',
            providesTags: ['interestRates'],
        }),
        getLendersWithInterestRates: builder.query<
            LenderWithInterestRates[],
            void
        >({
            query: () => 'lenders/interest-rates/',
            providesTags: ['interestRates'],
            transformResponse: (response: any) => {
                return response.data;
            },
        }),
        getFinanceApplicationResult: builder.query<
            IFinanceApplicationData[],
            string | undefined
        >({
            query: id => `/finance/${id}/thankyou`,
            transformResponse: (response: any) => {
                return response.data;
            },
        }),
        getBookedVehicleDetails: builder.query<
            IBookedVehicleDetails[],
            string | undefined
        >({
            query: id => `/bookings/${id}/vehicle-details`,
            transformResponse: (response: any) => {
                return response.data;
            },
        }),
        notifyMe: builder.mutation<any, any>({
            query: data => ({
                url: '/notifications',
                method: 'POST',
                body: data,
            }),
        }),
        submitFinancingApplication: builder.mutation<any, any>({
            query: data => ({
                url: 'finance/applications',
                method: 'POST',
                body: data,
            }),
        }),
        getMinAndMaxPrices: builder.query<MinAndMaxPrices, void>({
            query: () => ({
                url: 'vehicles/minMaxPrices',
                providesTags: ['MaxAndMinPrices'],
            }),
            transformResponse: (response: IMinAndMaxPricesResult) =>
                response.data,
        }),
        checkVisitValidity: builder.query<void, IVisitDetailsCheck>({
            query: ({ visitId }) => ({
                url: `iv/${visitId}?request_otp=1`,
            }),
        }),
        getVisitDetails: builder.query<IVisitDetailsData, IVisitDetails>({
            query: ({ visitId, otp }) => ({
                url: `iv/${visitId}?otp=${otp}`,
            }),
            transformResponse: (response: VisitDetailsResponse) => {
                return response.data;
            },
        }),
        getMinMaxInterestRates: builder.query<MinAndMaxInterestRates, void>({
            query: () => ({
                url: 'interest-rates/range',
                providesTags: ['MinMaxInterestRates'],
            }),
            transformResponse: (response: IMinAndMaxInterestRatesResult) =>
                response.data,
        }),
    }),
});

// Export the auto-generated hook for the getCarListing and getCarDetails query endpoint
const {
    useGetCarListingQuery,
    useGetCarDetailsQuery,
    useGetCitiesAreasQuery,
    useGetImperfectionReportQuery,
    useGetImperfectionReportNewQuery,
    useGetPopularBrandsQuery,
    useGetBodyTypesMutation,
    useLazyGetBookingAndVehicleDetailsQuery,
    useGetCarShowroomCalendarQuery,
    useLazyGetCarShowroomCalendarQuery,
    useValidateTimeSlotMutation,
    useSendOTPMutation,
    useGetInterestRatesQuery,
    useNotifyMeMutation,
    useSubmitFinancingApplicationMutation,
    useGetMinAndMaxPricesQuery,
    useGetSimilarCarsQuery,
    useGetLendersWithInterestRatesQuery,
    useGetFinanceApplicationResultQuery,
    useGetBookedVehicleDetailsQuery,
    useCheckVisitValidityQuery,
    useLazyGetVisitDetailsQuery,
    useGetMinMaxInterestRatesQuery,
} = apiSlice;

export {
    useGetCarListingQuery,
    useGetCarDetailsQuery,
    useGetCitiesAreasQuery,
    useGetImperfectionReportQuery,
    useGetImperfectionReportNewQuery,
    useGetPopularBrandsQuery,
    useGetBodyTypesMutation,
    useLazyGetBookingAndVehicleDetailsQuery,
    useGetCarShowroomCalendarQuery,
    useLazyGetCarShowroomCalendarQuery,
    useValidateTimeSlotMutation,
    useSendOTPMutation,
    useGetInterestRatesQuery,
    useNotifyMeMutation,
    useSubmitFinancingApplicationMutation,
    useGetMinAndMaxPricesQuery,
    useGetSimilarCarsQuery,
    useGetLendersWithInterestRatesQuery,
    useGetFinanceApplicationResultQuery,
    useGetBookedVehicleDetailsQuery,
    useCheckVisitValidityQuery,
    useLazyGetVisitDetailsQuery,
    useGetMinMaxInterestRatesQuery,
    useBookSlotMutation,
};
